// features/notifications/hooks/usePushNotifications.ts
//
// React surface over services/pushNotifications. Exposes whether push is
// supported, the current browser permission, the user's master toggle state
// (live from RTDB), and enable/disable actions.

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import {
  disablePush,
  enablePush,
  getPermission,
  isEnabledOnThisDevice,
  isPushSupported,
} from '../../../services/pushNotifications';

export interface UsePushNotificationsResult {
  supported: boolean;
  /** 'default' | 'granted' | 'denied' | 'unsupported' */
  permission: NotificationPermission | 'unsupported';
  /** Whether push is registered on THIS device (per-device, not per-account). */
  enabled: boolean;
  loading: boolean;
  busy: boolean;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
}

export function usePushNotifications(): UsePushNotificationsResult {
  const { user } = useAuth();
  const notify = useNotification();
  const uid = user?.uid;

  const supported = isPushSupported();
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    getPermission(),
  );
  // Per-device state: tokens are per-device, so "enabled" must reflect THIS
  // browser, not the shared per-account flag (which would falsely show "On"
  // on a phone just because the desktop enabled it).
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEnabled(isEnabledOnThisDevice());
    setLoading(false);
  }, [uid]);

  const enable = useCallback(async () => {
    if (!uid) return;
    setBusy(true);
    try {
      const result = await enablePush(uid);
      setPermission(getPermission());
      setEnabled(isEnabledOnThisDevice());
      if (result.granted) {
        notify.success('Push notifications enabled on this device');
      } else if (result.reason === 'denied') {
        notify.warning('Notifications are blocked. Enable them in your browser settings.');
      } else if (result.reason === 'unsupported') {
        notify.info('This browser does not support push notifications.');
      } else {
        notify.error('Could not enable notifications. Please try again.');
      }
    } catch (err) {
      notify.error('Could not enable notifications. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [uid, notify]);

  const disable = useCallback(async () => {
    if (!uid) return;
    setBusy(true);
    try {
      await disablePush(uid);
      setEnabled(isEnabledOnThisDevice());
      notify.info('Push notifications turned off on this device');
    } finally {
      setBusy(false);
    }
  }, [uid, notify]);

  return { supported, permission, enabled, loading, busy, enable, disable };
}

export default usePushNotifications;
